import { useCallback, useRef, useMemo, type MutableRefObject } from 'react';

import { useMenuItemDragAndDrop } from '@atlaskit/side-nav-items/drag-and-drop/use-menu-item-drag-and-drop';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import {
	getMoreMenuData,
	isL1ItemData,
	isL1ItemDataInGroup,
} from '@atlassian/navigation-drag-and-drop/dnd-data';
import { useElementRegistry } from '@atlassian/navigation-drag-and-drop/element-registry';

import { useOptions } from '../../../controllers/options-provider';

export function useMoreMenuDragAndDrop(): {
	dropIndicator: false | JSX.Element | null;
	dropTargetRef: MutableRefObject<HTMLDivElement | null>;
	moreButtonRef: (element: HTMLButtonElement | null) => void;
} {
	const { l1DragAndDropEnabled } = useOptions();

	const memoisedHookProps = useMemo<Parameters<typeof useMenuItemDragAndDrop>[0]>(() => {
		if (l1DragAndDropEnabled) {
			return {
				dropTarget: {
					getData: () => getMoreMenuData(),
					getOperations: () => ({
						combine: 'available',
					}),
					canDrop: ({ source }) => {
						return (
							isL1ItemData(source.data) &&
							isL1ItemDataInGroup(source.data, [L1_MENU_GROUPS.MENU, L1_MENU_GROUPS.SHORTCUTS]) &&
							source.data.isHideable
						);
					},
				},
			};
		}
		return {};
	}, [l1DragAndDropEnabled]);

	const { dropIndicator, dropTargetRef } = useMenuItemDragAndDrop(memoisedHookProps);

	const registry = useElementRegistry();

	const registrationRef = useRef<(() => void) | null>(null);
	const moreButtonRef = useCallback(
		(element: HTMLButtonElement | null): void => {
			if (!element) {
				registrationRef.current?.();
				registrationRef.current = null;

				return;
			}

			// Unregister previous element if exists
			if (registrationRef.current) {
				registrationRef.current();
			}

			// Register the new element with the registry
			registrationRef.current = registry.registerMoreMenuItem(element);
		},
		[registrationRef, registry],
	);

	return { dropIndicator, dropTargetRef, moreButtonRef };
}
